import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { LoadingIndicator } from '../ui/LoadingIndicator.js';
import { audioManager } from '../audio/AudioManager.js';
import { gameConfig } from '../config/gameConfig.js';

export class Scene {
    constructor() {
        // 创建场景
        this.scene = new THREE.Scene();
        
        // 检测是否为移动设备
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // 计算响应式缩放比例
        const screenWidth = window.innerWidth;
        const { baseScreenWidth, minScale, maxScale, mobileScale } = gameConfig.table.responsive;
        
        // 基于屏幕宽度计算基础缩放比例
        let scale = Math.min(maxScale, Math.max(minScale, screenWidth / baseScreenWidth));
        
        // 如果是移动设备，应用额外的缩放
        if (this.isMobile) {
            scale *= mobileScale;
        }
        
        // 创建相机并应用响应式缩放
        this.camera = new THREE.PerspectiveCamera(
            gameConfig.camera.fov,
            window.innerWidth / window.innerHeight,
            gameConfig.camera.near,
            gameConfig.camera.far
        );
        
        // 创建渲染器，添加性能优化配置
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
            precision: 'mediump',
            stencil: false
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setClearColor(0x000000, 0); // 设置透明背景
        
        // 启用阴影
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // 将渲染器的 canvas 添加到 DOM
        document.body.appendChild(this.renderer.domElement);

        // 配置触摸控制
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.touches = {
            ONE: THREE.TOUCH.NONE,  // 禁用单指控制
            TWO: THREE.TOUCH.DOLLY_ROTATE // 启用双指旋转和缩放
        };
        this.controls.enableZoom = gameConfig.camera.controls.enableZoom;
        this.controls.enableRotate = gameConfig.camera.controls.enableRotate;
        this.controls.enablePan = gameConfig.camera.controls.enablePan;
        
        // 设置缩放速度
        this.controls.zoomSpeed = gameConfig.camera.controls.zoomSpeed;
        
        // 设置旋转速度
        this.controls.rotateSpeed = gameConfig.camera.controls.rotateSpeed;
        
        // 设置缩放范围
        this.controls.minDistance = gameConfig.camera.controls.minDistance;
        this.controls.maxDistance = gameConfig.camera.controls.maxDistance;
        
        // 设置相机初始位置
        this.resetCamera()

        // 添加光源
        this.setupLights();

        // 添加窗口大小调整监听
        window.addEventListener('resize', () => this.onWindowResize());
    }

    setupLights() {
        // 环境光 - 提高强度至1.0
        const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
        this.scene.add(ambientLight);

        // 主方向光 - 调整位置和强度
        const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
        mainLight.position.set(10, 15, 10);
        mainLight.castShadow = true;
        this.scene.add(mainLight);

        // 补充光源 - 增强填充光
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
        fillLight.position.set(-8, 12, -8);
        this.scene.add(fillLight);

        // 从配置中获取指示器参数
        const { height: indicatorHeight, intensity: indicatorIntensity, distance: indicatorDistance, colors } = gameConfig.ui.indicators;

        // 导入字体加载器和思源黑体字体文件 前后左右
        const fontLoader = new FontLoader();
        const fontUrl = gameConfig.ui.fonts.sourceSans;
        console.log('开始加载字体文件:', fontUrl);

        // 创建文字指示器的函数
        const createTextIndicator = (text, color, position) => {
            console.log(`准备创建文字指示器 - 文本:"${text}", 颜色:${color.toString(16)}, 位置:`, position);
            return new Promise((resolve) => {
                fontLoader.load(fontUrl, (font) => {
                    console.log(`字体加载成功 - 文本:"${text}"`);
                    const textGeometry = new TextGeometry(text, {
                        font: font,
                        size: gameConfig.ui.fonts.size,
                        height: gameConfig.ui.fonts.height,
                        curveSegments: gameConfig.ui.fonts.curveSegments,
                        bevelEnabled: gameConfig.ui.fonts.bevel.enabled,
                        bevelThickness: gameConfig.ui.fonts.bevel.thickness,
                        bevelSize: gameConfig.ui.fonts.bevel.size,
                        bevelOffset: gameConfig.ui.fonts.bevel.offset,
                        bevelSegments: gameConfig.ui.fonts.bevel.segments
                    });

                    // 居中文字
                    textGeometry.computeBoundingBox();
                    const centerOffset = new THREE.Vector3();
                    textGeometry.boundingBox.getCenter(centerOffset);
                    textGeometry.translate(-centerOffset.x, -centerOffset.y, -centerOffset.z);

                    const material = new THREE.MeshStandardMaterial({
                        color: color,
                        emissive: color,
                        emissiveIntensity: 0.8 // 增加发光强度
                    });

                    const textMesh = new THREE.Mesh(textGeometry, material);
                    textMesh.position.copy(position);

                    // 根据位置调整文字朝向
                    if (position.x > 0) { // 右
                        textMesh.rotation.y = -Math.PI / 2;
                    } else if (position.x < 0) { // 左
                        textMesh.rotation.y = Math.PI / 2;
                    } else if (position.z < 0) { // 后
                        textMesh.rotation.y = Math.PI;
                    }

                    resolve(textMesh);
                });
            });
        };

        // 创建并添加所有文字指示器
        Promise.all([
            createTextIndicator('前', colors.front, new THREE.Vector3(0, indicatorHeight, indicatorDistance)),
            createTextIndicator('后', colors.back, new THREE.Vector3(0, indicatorHeight, -indicatorDistance)),
            createTextIndicator('左', colors.left, new THREE.Vector3(-indicatorDistance, indicatorHeight, 0)),
            createTextIndicator('右', colors.right, new THREE.Vector3(indicatorDistance, indicatorHeight, 0))
        ]).then(([frontIndicator, backIndicator, leftIndicator, rightIndicator]) => {
            // 添加文字指示器到场景
            this.scene.add(frontIndicator);
            this.scene.add(backIndicator);
            this.scene.add(leftIndicator);
            this.scene.add(rightIndicator);

            // 创建并添加对应的点光源
            const frontLight = new THREE.PointLight(colors.front, indicatorIntensity, indicatorDistance);
            frontLight.position.copy(frontIndicator.position);
            this.scene.add(frontLight);

            const backLight = new THREE.PointLight(colors.back, indicatorIntensity, indicatorDistance);
            backLight.position.copy(backIndicator.position);
            this.scene.add(backLight);

            const leftLight = new THREE.PointLight(colors.left, indicatorIntensity, indicatorDistance);
            leftLight.position.copy(leftIndicator.position);
            this.scene.add(leftLight);

            const rightLight = new THREE.PointLight(colors.right, indicatorIntensity, indicatorDistance);
            rightLight.position.copy(rightIndicator.position);
            this.scene.add(rightLight);

            // 保存指示灯引用以便后续可能的动画或交互
            this.directionIndicators = {
                front: { mesh: frontIndicator, light: frontLight },
                back: { mesh: backIndicator, light: backLight },
                left: { mesh: leftIndicator, light: leftLight },
                right: { mesh: rightIndicator, light: rightLight }
            };
        });
    }

    setCameraPosition(distance, animate = false, duration = 1000) {
        // 计算45度角的相机位置
        const height = distance * Math.sin(Math.PI / 4); // 高度 = 距离 × sin(45°)
        const depth = distance * Math.cos(Math.PI / 4);  // 深度 = 距离 × cos(45°)
        const targetPosition = new THREE.Vector3(0, height, depth);
        const targetLookAt = new THREE.Vector3(0, 0, 0);

        if (!animate) {
            // 直接设置相机位置和朝向
            this.camera.position.copy(targetPosition);
            this.camera.lookAt(targetLookAt);
            return;
        }

        // 获取当前相机位置和朝向
        const startPosition = this.camera.position.clone();
        const startQuaternion = this.camera.quaternion.clone();

        // 计算目标四元数（相机朝向）
        const targetQuaternion = new THREE.Quaternion();
        const m = new THREE.Matrix4();
        m.lookAt(targetPosition, targetLookAt, new THREE.Vector3(0, 1, 0));
        targetQuaternion.setFromRotationMatrix(m);

        const startTime = Date.now();

        const animateCamera = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // 使用缓动函数使动画更平滑
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            // 插值计算当前位置
            this.camera.position.lerpVectors(startPosition, targetPosition, easeProgress);

            // 使用四元数插值计算当前朝向
            this.camera.quaternion.slerpQuaternions(startQuaternion, targetQuaternion, easeProgress);

            if (progress < 1) {
                requestAnimationFrame(animateCamera);
            } else {
                // 动画结束后，确保相机完全对准目标点
                this.camera.lookAt(targetLookAt);
            }
        };

        animateCamera();
    }

    onWindowResize() {
        // 更新相机宽高比
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();

        // 更新渲染器大小
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        // 根据设备类型和屏幕比例调整相机位置
        const baseDistance = this.isMobile ? 30 : 20;
        const distance = Math.max(baseDistance, baseDistance * 1.4 * (window.innerWidth / window.innerHeight));
        this.setCameraPosition(distance);
    }

    resetCamera() {
        // 根据设备类型设置初始距离
        const initialDistance = this.isMobile ? gameConfig.camera.controls.mobileDistance : gameConfig.camera.controls.desktopDistance;
        this.setCameraPosition(initialDistance, true, 1000);
    }

    initResetCameraButton() {
        // 创建重置相机按钮
        const resetButton = document.createElement('button');
        resetButton.className = 'reset-camera-button';
        resetButton.textContent = '重置视角';
        document.body.appendChild(resetButton);

        // 添加点击事件监听器
        resetButton.addEventListener('click', () => this.resetCamera());
    }


    async preloadResources() {
        console.log('开始预加载游戏资源...');
        // 创建加载指示器
        const loadingIndicator = new LoadingIndicator();
        loadingIndicator.show();

        return new Promise((resolve, reject) => {
            // 创建加载器
            const textureLoader = new THREE.TextureLoader();
            const gltfLoader = new GLTFLoader();
            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
            gltfLoader.setDRACOLoader(dracoLoader);

            // 预加载所有资源
            const loadPromises = [];
            let totalProgress = 0;

            // 加载地毯纹理
            const carpetTexturePromise = new Promise((resolve, reject) => {
                textureLoader.load(
                    'textures/carpet.jpg',
                    (texture) => {
                        console.log('地毯纹理加载成功');
                        totalProgress = 50;
                        loadingIndicator.updateProgress(totalProgress, '地毯纹理加载完成');
                        resolve(texture);
                    },
                    (xhr) => {
                        const progress = (xhr.loaded / xhr.total * 100) * 0.5;
                        loadingIndicator.updateProgress(progress, '正在加载地毯纹理...');
                    },
                    (error) => {
                        console.error('地毯纹理加载失败：', error);
                        reject(error);
                    }
                );
            });
            loadPromises.push(carpetTexturePromise);

            // 预加载骰子模型
            const diceModelPromise = new Promise((resolve, reject) => {
                gltfLoader.load(
                    'models/dice-draco-1.glb',
                    (gltf) => {
                        console.log('骰子模型加载成功');
                        totalProgress = 100;
                        loadingIndicator.updateProgress(totalProgress, '骰子模型加载完成');
                        resolve(gltf);
                    },
                    (xhr) => {
                        const progress = 50 + (xhr.loaded / xhr.total * 100) * 0.5;
                        loadingIndicator.updateProgress(progress, '正在加载骰子模型...');
                    },
                    (error) => {
                        console.error('骰子模型加载失败：', error);
                        reject(error);
                    }
                );
            });
            loadPromises.push(diceModelPromise);

            // 预加载背景音乐
            const bgmPromise = new Promise((resolve, reject) => {
                const audio = new Audio();
                audio.src = 'audio/bgm_sunny.mp3';
                
                // 添加progress事件监听器来跟踪下载进度
                audio.addEventListener('progress', (event) => {
                    if (audio.buffered.length > 0) {
                        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
                        const duration = audio.duration;
                        if (duration > 0) {
                            const progress = (bufferedEnd / duration) * 100;
                            const totalProgress = 50 + (progress * 0.5); // 音频加载占总进度的50%
                            loadingIndicator.updateProgress(totalProgress, '正在加载背景音乐...');
                        }
                    }
                });
                
                audio.addEventListener('canplaythrough', () => {
                    console.log('背景音乐加载成功');
                    resolve(audio);
                });
                audio.addEventListener('error', (error) => {
                    console.error('背景音乐加载失败：', error);
                    reject(error);
                });
                audio.load();
            });
            loadPromises.push(bgmPromise);

            // 等待所有资源加载完成
            Promise.all(loadPromises)
                .then((resources) => {
                    console.log('所有游戏资源加载完成');
                    this.preloadedResources = {
                        carpetTexture: resources[0],
                        diceModel: resources[1],
                        bgm: resources[2]
                    };
                    loadingIndicator.hide();
                    resolve();
                })
                .catch(error => {
                    loadingIndicator.hide();
                    reject(error);
                });
        });

    }

    async init() {
        // 先预加载所有资源
        await this.preloadResources();
        
        // 初始化重置相机按钮
        this.initResetCameraButton();

        // 使用预加载的地毯纹理
        const carpetTexture = this.preloadedResources.carpetTexture;
        carpetTexture.wrapS = THREE.RepeatWrapping;
        carpetTexture.wrapT = THREE.RepeatWrapping;
        carpetTexture.repeat.set(8, 8);

        // 计算基准尺寸
        const baseSize = this.isMobile ? 12 : 16; // 移动设备使用更小的基准尺寸
        
        // 计算各部分尺寸
        const carpetSize = baseSize * 6;  // 地毯尺寸是基准的6倍
        const surfaceSize = baseSize;     // 表面尺寸等于基准尺寸
        const edgeWidth = baseSize * 0.06;  // 边缘宽度是基准尺寸的6%
        const baseExtraSize = baseSize * 0.03;  // 底座额外大小是基准尺寸的3%

        // 创建地毯
        const carpetGeometry = new THREE.CircleGeometry(carpetSize, 64);
        const carpetMaterial = new THREE.MeshStandardMaterial({
            map: carpetTexture,
            bumpMap: carpetTexture,
            bumpScale: 0.02,
            side: THREE.DoubleSide,
            roughness: 0.8,
            metalness: 0.1
        });
        const carpet = new THREE.Mesh(carpetGeometry, carpetMaterial);
        carpet.rotation.x = -Math.PI / 2;
        carpet.position.y = gameConfig.table.carpetHeight;
        carpet.receiveShadow = true;

        // 创建牌桌表面（深绿色绒面）
        const tableSurfaceGeometry = new THREE.CircleGeometry(surfaceSize, 64);
        const tableSurfaceMaterial = new THREE.MeshStandardMaterial({
            color: 0x006400,  // 使用深绿色
            side: THREE.DoubleSide,
            roughness: 0.7,    // 适当的粗糙度模拟绒面
            metalness: 0.05    // 降低金属度增加绒面感
        });
        const tableSurface = new THREE.Mesh(tableSurfaceGeometry, tableSurfaceMaterial);
        tableSurface.rotation.x = -Math.PI / 2;
        tableSurface.position.y = gameConfig.table.surfaceHeight;
        tableSurface.receiveShadow = true;

        // 创建牌桌边缘（深色皮革软垫）
        const edgeGeometry = new THREE.TorusGeometry(surfaceSize, edgeWidth, 16, 64);
        const edgeMaterial = new THREE.MeshStandardMaterial({
            color: 0x2b1810,
            roughness: 0.9,
            metalness: 0.1,
            envMapIntensity: 0.5
        });
        const tableEdge = new THREE.Mesh(edgeGeometry, edgeMaterial);
        tableEdge.rotation.x = -Math.PI / 2;
        tableEdge.position.y = gameConfig.table.edgeHeight;
        tableEdge.receiveShadow = true;

        // 创建牌桌底座
        const baseGeometry = new THREE.CircleGeometry(surfaceSize + baseExtraSize, 64);
        const baseMaterial = new THREE.MeshStandardMaterial({
            color: 0x1a0f0f,
            roughness: 0.7,
            metalness: 0.2
        });
        const tableBase = new THREE.Mesh(baseGeometry, baseMaterial);
        tableBase.rotation.x = -Math.PI / 2;
        tableBase.position.y = gameConfig.table.baseHeight;
        tableBase.receiveShadow = true;

        // 将所有部件添加到场景
        this.scene.add(tableBase);
        this.scene.add(tableEdge);
        this.scene.add(tableSurface);
        this.scene.add(carpet);
    }
}